import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, Renderer2, SimpleChanges, ViewChild } from '@angular/core';
import { ApexAnnotations, ApexChart, ApexDataLabels, ApexFill, ApexForecastDataPoints, ApexGrid, ApexLegend, ApexMarkers, ApexNoData, ApexPlotOptions, ApexResponsive, ApexParsing, ApexAxisChartSeries, ApexNonAxisChartSeries, ApexStates, ApexStroke, ApexTitleSubtitle, ApexTheme, ApexTooltip, ApexXAxis, ApexYAxis, ApexOptions } from 'apexcharts';
import * as ApexCharts from 'apexcharts';
import { BehaviorSubject, debounceTime, Subject } from 'rxjs';

@Component({
    selector: 'is-chart',
    templateUrl: './is-chart.component.html',
    styleUrls: ['is-chart.component.scss']
})
export class ISChartComponent implements OnInit, OnDestroy {

    private inputChangeFlag$ = new Subject<void>();
    chartUnlisten?: () => void;
    private renderTimeout?: ReturnType<typeof setTimeout>;
    private destroyed = false;

    @ViewChild('chart', { static: true }) set ApexChartElement(c: ElementRef) {
        if (c) {

            if (!this.apexChart && this.renderTimeout === undefined) {

                this.renderTimeout = setTimeout(() => {
                    this.apexChart = new (ApexCharts as any).default(c.nativeElement, this.chartOptions)
                    this.apexChart.render().then( () => {
                        // The component may have been destroyed while the chart was rendering
                        if (this.destroyed) {
                            return;
                        }

                        const element = c.nativeElement as HTMLElement;
                        const preventWheel = (event: WheelEvent) => event.preventDefault();

                        element.addEventListener('wheel', preventWheel, {passive: false});
                        this.chartUnlisten = () => element.removeEventListener('wheel', preventWheel);
                    });
                }, 0)
            }
        }
    }

    @Input() set annotations(v: ApexAnnotations | undefined) {
        this.chartOptions.annotations = v;
        this.inputChangeFlag$.next()
    };

    @Input() set chart(v: ApexChart | undefined) {
        this.chartOptions.chart = v;
        this.inputChangeFlag$.next()
    };

    @Input() set colors(v: any[] | undefined) {
        this.chartOptions.colors = v;
        this.inputChangeFlag$.next()
    };

    @Input() set dataLabels(v: ApexDataLabels | undefined) {
        this.chartOptions.dataLabels = v;
        this.inputChangeFlag$.next()
    };

    @Input() set fill(v: ApexFill | undefined) {
        this.chartOptions.fill = v;
        this.inputChangeFlag$.next()
    };

    @Input() set forecastDataPoints(v: ApexForecastDataPoints | undefined) {
        this.chartOptions.forecastDataPoints = v;
        this.inputChangeFlag$.next()
    };

    @Input() set grid(v: ApexGrid | undefined) {
        this.chartOptions.grid = v;
        this.inputChangeFlag$.next()
    };

    @Input() set labels(v: string[] | undefined) {
        this.chartOptions.labels = v;
        this.inputChangeFlag$.next()
    };

    @Input() set legend(v: ApexLegend | undefined) {
        this.chartOptions.legend = v;
        this.inputChangeFlag$.next()
    };

    @Input() set markers(v: ApexMarkers | undefined) {
        this.chartOptions.markers = v;
        this.inputChangeFlag$.next()
    };

    @Input() set noData(v: ApexNoData | undefined) {
        this.chartOptions.noData = v;
        this.inputChangeFlag$.next()
    };

    @Input() set plotOptions(v: ApexPlotOptions | undefined) {
        this.chartOptions.plotOptions = v;
        this.inputChangeFlag$.next()
    };

    @Input() set responsive(v: ApexResponsive[] | undefined) {
        this.chartOptions.responsive = v;
        this.inputChangeFlag$.next()
    };

    @Input() set parsing(v: ApexParsing | undefined) {
        this.chartOptions.parsing = v;
        this.inputChangeFlag$.next()
    };

    @Input() set series(v: ApexAxisChartSeries | ApexNonAxisChartSeries | undefined) {
        this.chartOptions.series = v;
        this.inputChangeFlag$.next()
    };

    @Input() set states(v: ApexStates | undefined) {
        this.chartOptions.states = v;
        this.inputChangeFlag$.next()
    };

    @Input() set stroke(v: ApexStroke | undefined) {
        this.chartOptions.stroke = v;
        this.inputChangeFlag$.next()
    };

    @Input() set subtitle(v: ApexTitleSubtitle | undefined) {
        this.chartOptions.subtitle = v;
        this.inputChangeFlag$.next()
    };

    @Input() set theme(v: ApexTheme | undefined) {
        this.chartOptions.theme = v;
        this.inputChangeFlag$.next()
    };
    @Input() set title(v: ApexTitleSubtitle | undefined) {
        this.chartOptions.title = v;
        this.inputChangeFlag$.next()
    };

    @Input() set tooltip(v: ApexTooltip | undefined) {
        this.chartOptions.tooltip = v;
        this.inputChangeFlag$.next()
    };

    @Input() set xaxis(v: ApexXAxis | undefined) {
        this.chartOptions.xaxis = v;
        this.inputChangeFlag$.next()
    };

    @Input() set yaxis(v: ApexYAxis | ApexYAxis[] | undefined) {
        this.chartOptions.yaxis = v;
        this.inputChangeFlag$.next()
    };

    private chartOptions: ApexOptions = {};

    protected componentKey = crypto.randomUUID();
    private apexChart!: ApexCharts

    constructor(private _renderer: Renderer2) {
    }

    ngOnInit(): void { 
        // Inputs can change before the chart is created (it renders on a timeout), in which case the constructor already gets the latest options
        this.inputChangeFlag$.pipe(debounceTime(0)).subscribe( () =>
            this.apexChart?.updateOptions(this.chartOptions)
        )
    }

    // ApexCharts keeps window listeners that hold the chart (and the options' formatter closures) in memory until destroy() is called
    ngOnDestroy(): void {
        this.destroyed = true;
        clearTimeout(this.renderTimeout);
        this.inputChangeFlag$.complete();
        this.chartUnlisten?.();
        this.apexChart?.destroy();
    }

    public get methods(): ApexCharts {
        return this.apexChart;
    }

    public get options(): ApexOptions {
        return this.chartOptions;
    }

    public get annotations(): ApexAnnotations {
        return this.chartOptions.annotations!;
    }

    public get chart(): ApexChart {
        return this.chartOptions.chart!;
    }

    public get colors(): any[] {
        return this.chartOptions.colors!;
    }

    public get dataLabels(): ApexDataLabels {
        return this.chartOptions.dataLabels!;
    }

    public get fill(): ApexFill {
        return this.chartOptions.fill!;
    }

    public get forecastDataPoints(): ApexForecastDataPoints {
        return this.chartOptions.forecastDataPoints!;
    }

    public get grid(): ApexGrid {
        return this.chartOptions.grid!;
    }

    public get labels(): string[] {
        return this.chartOptions.labels!;
    }

    public get legend(): ApexLegend {
        return this.chartOptions.legend!;
    }

    public get markers(): ApexMarkers {
        return this.chartOptions.markers!;
    }

    public get noData(): ApexNoData {
        return this.chartOptions.noData!;
    }

    public get plotOptions(): ApexPlotOptions {
        return this.chartOptions.plotOptions!;
    }

    public get responsive(): ApexResponsive[] {
        return this.chartOptions.responsive!;
    }

    public get parsing(): ApexParsing {
        return this.chartOptions.parsing!;
    }

    public get series(): ApexAxisChartSeries | ApexNonAxisChartSeries {
        return this.chartOptions.series!;
    }

    public get states(): ApexStates {
        return this.chartOptions.states!;
    }

    public get stroke(): ApexStroke {
        return this.chartOptions.stroke!;
    }

    public get subtitle(): ApexTitleSubtitle {
        return this.chartOptions.subtitle!;
    }

    public get theme(): ApexTheme {
        return this.chartOptions.theme!;
    }

    public get title(): ApexTitleSubtitle {
        return this.chartOptions.title!;
    }

    public get tooltip(): ApexTooltip {
        return this.chartOptions.tooltip!;
    }

    public get xaxis(): ApexXAxis {
        return this.chartOptions.xaxis!;
    }

    public get yaxis(): ApexYAxis | ApexYAxis[] {
        return this.chartOptions.yaxis!;
    }
}
